import React from "react";
import {withTranslation, WithTranslation} from "react-i18next";
import {connect} from "react-redux";
import {bindActionCreators, Dispatch} from "redux";
import {
    BulkProducts, GetBulkProductSearchDataResponse, ProductMatchPageResponse,
} from "src/model/ProductMatch";
import MetricsUtil from "src/utils/MetricsUtil";
import ApplicationState from "src/model/ApplicationState";
import {
    KatButton,
    KatButtonGroup,
    KatExpander,
    KatLabel,
} from "@amzn/katal-react";
import SearchMyProductBarComponent from "src/components/bulk/SearchMyProductBarComponent";
import BulkProductTableComponent from "src/components/bulk/BulkProductTableComponent";
import {bulkProgramName, isLoginMode, PROGRAM_NAME_OF_FBA} from "src/config/Config";
import SearchAmznProductBarComponent from "src/components/bulk/SearchAmznProductBarComponent";
import BulkProductProgramTableComponent from "src/components/bulk/BulkProductProgramTableComponent";
import ErrorStatusComponent from "src/components/common/ErrorStatusComponent";
import RequestStatus from "src/model/RequestStatus";
import DownloadReportComponent from "src/components/bulk/DownloadReportComponent";
import TemplateUploadComponent from "src/components/bulk/TemplateUploadComponent";


interface StateProps {
    bulkProductsRes: BulkProducts,
    bulkProductSearchRes: GetBulkProductSearchDataResponse,
}

interface OwnProps {
    readonly currentCountryCode: string,
    readonly metricsUtil?: MetricsUtil,
    handleCountryChange: (countryCode: string) => void
}

const mapStateToProps = (state: ApplicationState): StateProps => ({
    bulkProductsRes: state.bulkProductsRes,
    bulkProductSearchRes: state.bulkProductSearchRes,
});

interface DispatchProps {
}

const mapDispatchToProps = (dispatch: Dispatch) => (
    bindActionCreators({}, dispatch)
);

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = StateProps & OwnProps & DispatchProps & WithTranslation;

interface State {
    estimateFor: string,
    step: number
}

export class BulkSearchComponent extends React.Component<Props, State> {

    protected readonly metricsUtil: MetricsUtil;

    constructor(props: Props) {
        super(props);
        this.state = {
            estimateFor: isLoginMode() ? "yourProducts" : "amazonCatalog",
            step: 1
        }

        if (this.props.metricsUtil) {
            this.metricsUtil = this.props.metricsUtil;
        } else {
            this.metricsUtil = MetricsUtil.getDefaultMetricUtil();
        }
        this.handleButtonChange = this.handleButtonChange.bind(this);
        this.handleNext = this.handleNext.bind(this);
        this.handleStep1Next = this.handleStep1Next.bind(this);
        this.handleStep2Next = this.handleStep2Next.bind(this);
        this.handleStep3Next = this.handleStep3Next.bind(this);
        this.handleStepChange = this.handleStepChange.bind(this);
    }


    handleButtonChange(event: KatButtonGroup.ChangeEvent) {
        this.setState({estimateFor: event.detail.value});
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter(event.detail.value, "click");
        }
    }

    handleStep1Next() {
        this.handleNext("step1");
    }

    handleStep2Next() {
        this.handleNext("step2");
    }

    handleStep3Next() {
        this.handleNext("step3");
    }

    handleNext(name: string) {
        this.setState({step: this.state.step + 1});
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("nextButton_" + name, "click");
        }
    }

    handleStepChange(step: number) {
        this.setState({step: step});
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("stepExpander", "click");
        }
    }

    hasError(): boolean {
        const hasError = this.props.bulkProductSearchRes && this.props.bulkProductSearchRes.requestStatus === RequestStatus.ERROR;
        if (hasError && this.metricsUtil) {
            this.metricsUtil.publishCounter("searchedBulkProducts", "error");
        }
        return hasError;
    }

    isEmpty(): boolean {
        if (this.props.bulkProductSearchRes && this.props.bulkProductSearchRes.requestStatus === RequestStatus.SUCCESS) {
            const productData: ProductMatchPageResponse = this.props.bulkProductSearchRes.productData;
            return productData == null || productData.data == null || productData.data.products == null || productData.data.products.length == 0;
        }
        return false;
    }

    render() {
        const {t} = this.props;
        const programNum = this.props.bulkProductsRes.programs.length;
        const hasError = this.hasError();
        const isEmpty = this.isEmpty();

        return (
            <>
                <div className="product-search-content-box">
                    <div id="ProductSearchFuncHeader" className="block">
                        <div className="product-search-element"><KatLabel text={t("SC_FBA_Calc_bulk_bulk_estimate_for")}
                                                                          className="product-search-func-header"/></div>
                        <div className="product-search-element"><KatLabel text={t('SC_FBA_Calc_info_search')}
                                                                          className="revenue-calculator-header-content"/></div>
                    </div>
                    <p className="bulk-search-button-group-title">{t("SC_FBA_Calc_bulk_bulk_estimate_for")}</p>
                    <KatButtonGroup value={this.state.estimateFor} onChange={this.handleButtonChange}>
                        {isLoginMode() && <KatButton label={t("SC_FBA_Calc_label_productsISell")} value="yourProducts"/>}
                        <KatButton label={t("SC_FBA_Calc_tab_search")} value="amazonCatalog"/>
                        {/*todo weblab for new product*/}
                        <KatButton label={t("SC_FBA_Calc_bulk_new_product")} value="new"/>
                    </KatButtonGroup>

                    {this.state.estimateFor != "new" &&
                    <>
                        <KatExpander expanded={this.state.step == 1} className="product-bulk-search-expander"
                                     label={t("SC_FBA_Calc_bulk_step") + " 1 " + t("SC_FBA_Calc_bulk_find_products")}
                                     onExpand={() => this.handleStepChange(1)}>
                            {this.state.step > 1 && <div slot="badge">
                                <div className="bulk-search-step1-info">
                                    <p>{t("SC_FBA_Calc_info_productCount", {X: this.props.bulkProductsRes.products.length})}</p>
                                    <p>{t("SC_FBA_Calc_bulk_program") + " 1: " + t(bulkProgramName[this.props.bulkProductsRes.programs[0]][0])}</p>
                                    {this.props.bulkProductsRes.programs.length > 1 &&
                                    <p>{t("SC_FBA_Calc_bulk_program") + " 2: " + t(bulkProgramName[this.props.bulkProductsRes.programs[1]][0])}</p>
                                    }
                                </div>
                            </div>}
                            {this.state.estimateFor == "yourProducts" &&
                            <SearchMyProductBarComponent currentCountryCode={this.props.currentCountryCode}
                                                         metricsUtil={this.metricsUtil}
                                                         handleCountryChange={this.props.handleCountryChange}/>}
                            {this.state.estimateFor == "amazonCatalog" &&
                            <SearchAmznProductBarComponent currentCountryCode={this.props.currentCountryCode}
                                                           metricsUtil={this.metricsUtil}
                                                           handleCountryChange={this.props.handleCountryChange}/>}
                            {hasError && <ErrorStatusComponent description={t('SC_FBA_Calc_error_productMatchFailed')}/>}
                            {isEmpty && (<ErrorStatusComponent description={t('SC_FBA_Calc_product_emptyResult')}/>)}
                            <BulkProductTableComponent currentCountryCode={this.props.currentCountryCode}
                                                       metricsUtil={this.metricsUtil} handleNext={this.handleStep1Next}/>
                        </KatExpander>

                        {programNum > 0 &&
                        <KatExpander expanded={this.state.step == 2} className="product-bulk-search-expander"
                                     label={t("SC_FBA_Calc_bulk_step") + " 2 " + t("SC_FBA_Calc_bulk_step2_info") + " 1"}
                                     onExpand={() => this.handleStepChange(2)}>
                            <BulkProductProgramTableComponent currentCountryCode={this.props.currentCountryCode}
                                                              metricsUtil={this.metricsUtil}
                                                              program={this.props.bulkProductsRes.programs[0] ?? PROGRAM_NAME_OF_FBA}
                                                              handleNext={this.handleStep2Next}/>
                        </KatExpander>
                        }

                        {programNum == 2 &&
                        <KatExpander expanded={this.state.step == 3} className="product-bulk-search-expander"
                                     label={t("SC_FBA_Calc_bulk_step") + " 3 " + t("SC_FBA_Calc_bulk_step2_info") + " 2"}
                                     onExpand={() => this.handleStepChange(3)}>
                            <BulkProductProgramTableComponent currentCountryCode={this.props.currentCountryCode}
                                                              metricsUtil={this.metricsUtil}
                                                              program={this.props.bulkProductsRes.programs[1]}
                                                              handleNext={this.handleStep3Next}/>
                        </KatExpander>
                        }

                        {programNum > 0 &&
                        <KatExpander expanded={this.state.step == programNum + 2} className="product-bulk-search-expander"
                                     label={t("SC_FBA_Calc_bulk_step") + " " + (programNum + 2) + " " + t("SC_FBA_Calc_btn_download")}
                                     onExpand={() => this.handleStepChange(programNum + 2)}>
                            <DownloadReportComponent metricsUtil={this.metricsUtil} currentCountryCode={this.props.currentCountryCode}
                                                     handleStepChange={this.handleStepChange}/>

                        </KatExpander>
                        }
                    </>
                    }
                    {this.state.estimateFor == "new" && <TemplateUploadComponent/>}
                </div>
            </>
        );
    }
}

export default withTranslation()(connector(BulkSearchComponent));
